import React, { useCallback, useRef } from "react";

import Button, { ButtonTypes } from "../../../components/Button";
import { Skeleton } from "../../../components/Skeleton";
import { EmptyState } from "../../../components/States";
import { ObservedAt } from "../components/ObservedAt";
import { RefreshFailedNotice } from "../components/RefreshFailedNotice";
import { SectionUnavailable } from "../components/SectionUnavailable";
import { useDetailNavigation } from "../hooks/useDetailNavigation";
import {
  Caption,
  Card,
  PageContent,
  PageHeader,
  PageHeading,
  PageTitle,
  QueueLayout,
} from "../styles";
import { plural } from "../utils/format";
import { InstrumentList } from "./components/InstrumentList";
import { InstrumentsToolbar } from "./components/InstrumentsToolbar";
import { SelectionPane } from "./components/SelectionPane";
import { useInstrumentCatalog } from "./hooks/useInstrumentCatalog";
import { useInstrumentEdits } from "./hooks/useInstrumentEdits";
import { useInstrumentLink } from "./hooks/useInstrumentLink";
import { useInstrumentCatalogSession } from "./InstrumentCatalogProvider";
import { CatalogColumn } from "./styles";

export const InstrumentsPage: React.FC = () => {
  const {
    section,
    filters,
    instruments,
    selection,
    total,
    isFiltered,
    changeFilters,
    clearFilters,
    isRefreshing,
    refresh,
  } = useInstrumentCatalog();
  const { selectedId, drafts, select } = useInstrumentCatalogSession();
  const edits = useInstrumentEdits();
  const { isStacked, listRef, detailRef, openDetail, backToList } =
    useDetailNavigation();
  const isOpenPending = useRef(false);
  const openSelection = useCallback(
    (instrumentId: number) => {
      isOpenPending.current = true;
      select(instrumentId);
    },
    [select],
  );
  useInstrumentLink(openSelection);

  const isAvailable = section.state === "available";
  const isEmpty = isAvailable && instruments.length === 0;
  const refreshLabel = isRefreshing ? "Refreshing…" : "Refresh";
  const draftIds = new Set(Object.keys(drafts).map(Number));
  const isCapped = instruments.length < total;
  const countLabel = isCapped
    ? `Showing ${instruments.length} of ${total}, narrow the filters`
    : plural(total, "instrument");

  const handleSelect = (instrumentId: number) => {
    if (instrumentId === selectedId) {
      openDetail();
    } else {
      openSelection(instrumentId);
    }
  };
  // On a stacked layout the pane has no height until its editor renders, so scrolling earlier goes nowhere.
  const handleSelectionShown = useCallback(() => {
    if (isOpenPending.current) {
      isOpenPending.current = false;
      openDetail();
    }
  }, [openDetail]);
  const handleClose = () => {
    backToList();
    select(null);
  };

  return (
    <PageContent>
      <PageHeader>
        <PageTitle>
          <PageHeading>Instruments</PageHeading>
          <Caption>The full catalog, in symbol order.</Caption>
          {isAvailable && <ObservedAt observedAt={section.observedAt} />}
        </PageTitle>
        <Button
          type={ButtonTypes.cancel}
          size="small"
          disabled={isRefreshing}
          onClick={refresh}
        >
          {refreshLabel}
        </Button>
      </PageHeader>
      <InstrumentsToolbar
        filters={filters}
        countLabel={isAvailable ? countLabel : null}
        onChange={changeFilters}
      />
      {isAvailable && section.isRefreshFailed && (
        <RefreshFailedNotice sectionName="Instruments" onRetry={refresh} />
      )}
      <QueueLayout>
        <CatalogColumn>
          {section.state === "loading" && (
            <Skeleton variant="rectangular" height="12rem" />
          )}
          {section.state === "unavailable" && (
            <Card aria-label="Instruments">
              <SectionUnavailable
                observedAt={section.observedAt}
                onRetry={refresh}
              />
            </Card>
          )}
          {isEmpty && isFiltered && (
            <EmptyState
              title="No instruments match"
              description="Change the search or filters to see other instruments."
              action={
                <Button
                  type={ButtonTypes.cancel}
                  size="small"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              }
            />
          )}
          {isEmpty && !isFiltered && (
            <EmptyState
              title="No instruments yet"
              description="Instruments will appear here once they are added to the catalog."
            />
          )}
          {isAvailable && !isEmpty && (
            <InstrumentList
              instruments={instruments}
              selectedId={selectedId}
              draftIds={draftIds}
              isDisabled={edits.isSaving}
              listRef={listRef}
              onSelect={handleSelect}
            />
          )}
        </CatalogColumn>
        <SelectionPane
          selectedId={selectedId}
          selection={selection}
          isListSettled={section.state !== "loading"}
          onSelectionShown={handleSelectionShown}
          detailRef={detailRef}
          edits={edits}
          isFiltered={isFiltered}
          isStacked={isStacked}
          onClearFilters={clearFilters}
          onClose={handleClose}
          onBackToList={backToList}
        />
      </QueueLayout>
    </PageContent>
  );
};
